import { useState, useEffect, useCallback } from 'react';

// Generate a consistent seed count (double digits) for each property
// Based on a simple hash of the gohimalayan_id — always returns same number for same property
function getSeedCount(id) {
  if (!id) return 24;
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash |= 0;
  }
  return 18 + Math.abs(hash % 72); // Range: 18–89
}

const WISHLIST_KEY = 'gohimalayan_wishlist';
const LIKES_KEY = 'gohimalayan_likes';

function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
  } catch { return []; }
}

function getLikes() {
  try {
    return JSON.parse(localStorage.getItem(LIKES_KEY) || '{}');
  } catch { return {}; }
}

export function useWishlist() {
  const [wishlist, setWishlist] = useState(getWishlist);
  const [likes, setLikes] = useState(getLikes);

  useEffect(() => {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    localStorage.setItem(LIKES_KEY, JSON.stringify(likes));
  }, [likes]);

  const isWishlisted = useCallback((id) => wishlist.includes(id), [wishlist]);

  const toggleWishlist = useCallback((id) => {
    setWishlist(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      return [...prev, id];
    });
    setLikes(prev => {
      const current = prev[id] || 0;
      if (wishlist.includes(id)) {
        // Removing — decrement user's like
        return { ...prev, [id]: Math.max(0, current - 1) };
      }
      // Adding — increment
      return { ...prev, [id]: current + 1 };
    });
  }, [wishlist]);

  const getLikeCount = useCallback((id) => {
    const seed = getSeedCount(id);
    const userLikes = likes[id] || 0;
    return seed + userLikes;
  }, [likes]);

  return { wishlist, isWishlisted, toggleWishlist, getLikeCount };
}
